import { Box, Typography } from '@mui/material';
import BugReportIcon from '@mui/icons-material/BugReport';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import type { FC } from 'react';
import { useMemo, useState } from 'react';
import { Table } from 'src/common';
import type { TableData } from 'src/types/table';
import { SecurityDashboardCard } from '../../shared/securityDashboardCard';
import { useSecurityDashboardGrid } from '../../shared/useSecurityDashboardGrid';
import type { EndpointEventKpi, EventSeverityStat } from './endpointDetailsTypes';
import {
  buildEventColumns,
  buildTableFilterFields,
  createEmptyTableFilters,
  EndpointTableFiltersToolbar,
  EVENT_TABLE_FILTER_IDS,
  EVENT_TABLE_FILTER_LABELS,
  KpiCell,
  StatColumn,
  TablePaginationFooter,
} from './shared/endpointTableTab';

const EMPTY_FILTERS = createEmptyTableFilters(EVENT_TABLE_FILTER_IDS);
const EMPTY_FILTER_OPTIONS: Record<(typeof EVENT_TABLE_FILTER_IDS)[number], string[]> = {
  severity: [],
  category: [],
  status: [],
  user: [],
  source: [],
};

const EMPTY_EVENT_KPIS: EndpointEventKpi[] = [
  { id: 'total', label: 'Total Events', value: 0, trend: '0%', trendDirection: 'up' },
  { id: 'critical', label: 'Critical Events', value: 0, trend: '0%', trendDirection: 'up' },
  { id: 'security', label: 'Security Events', value: 0, trend: '0%', trendDirection: 'up' },
  { id: 'informational', label: 'Informational Events', value: 0, trend: '0%', trendDirection: 'up' },
];

const EMPTY_SEVERITY_DISTRIBUTION: EventSeverityStat[] = [
  { id: 'critical', label: 'Critical', value: 0, trend: '0%' },
  { id: 'high', label: 'High', value: 0, trend: '0%' },
  { id: 'low', label: 'Low', value: 0, trend: '0%' },
];

/** Placeholder until endpoint events APIs are wired. */
const EndpointEventsTab: FC = () => {
  const { portfolioKpiColumns, isMdDown } = useSecurityDashboardGrid();
  const [dateRange, setDateRange] = useState('');

  const filterFields = useMemo(
    () => buildTableFilterFields(EVENT_TABLE_FILTER_LABELS, EMPTY_FILTER_OPTIONS),
    []
  );
  const columns = useMemo(() => buildEventColumns(), []);

  const topRowColumns = isMdDown ? 'minmax(0, 1fr)' : 'minmax(0, 1.6fr) minmax(0, 1fr)';

  return (
    <Box display="flex" flexDirection="column" style={{ gap: 16, marginTop: 8, minWidth: 0 }}>
      <Box
        style={{
          display: 'grid',
          gridTemplateColumns: topRowColumns,
          gap: 16,
          alignItems: 'stretch',
          minWidth: 0,
        }}
      >
        <SecurityDashboardCard
          title="Portfolio Health"
          icon={<FavoriteBorderIcon style={{ fontSize: 18 }} />}
          headerRight={
            <Typography style={{ fontSize: 11, color: '#A0AEC0' }}>Updated: Just now</Typography>
          }
          style={{ height: '100%', minWidth: 0 }}
        >
          <Box sx={{ display: 'grid', gridTemplateColumns: portfolioKpiColumns, py: 2 }}>
            {EMPTY_EVENT_KPIS.map((kpi, index) => (
              <KpiCell key={kpi.id} kpi={kpi} showDivider={index > 0 && !isMdDown} />
            ))}
          </Box>
        </SecurityDashboardCard>

        <SecurityDashboardCard
          title="Event Severity Distribution"
          icon={<BugReportIcon style={{ fontSize: 18 }} />}
          style={{ height: '100%', minWidth: 0 }}
        >
          <Box display="flex" sx={{ py: 2 }}>
            {EMPTY_SEVERITY_DISTRIBUTION.map((stat, index) => (
              <StatColumn
                key={stat.id}
                label={stat.label}
                value={stat.value}
                trend={stat.trend}
                showDivider={index > 0}
              />
            ))}
          </Box>
        </SecurityDashboardCard>
      </Box>

      <SecurityDashboardCard
        overflow="hidden"
        footerAlign="space-between"
        footer={(
          <TablePaginationFooter
            page={0}
            pageCount={1}
            showingFrom={0}
            showingTo={0}
            total={0}
            onPageChange={() => undefined}
          />
        )}
      >
        <EndpointTableFiltersToolbar
          dateRange={dateRange}
          onDateRangeChange={setDateRange}
          fields={filterFields}
          selected={EMPTY_FILTERS}
          onChange={() => undefined}
        />

        <Table
          columns={columns}
          data={[] as TableData[]}
          showCheckbox
          selectedRows={[]}
          onSelectRows={() => undefined}
          showPagination={false}
          TableContainerProps={{ sx: { boxShadow: 'none', borderRadius: 0 } }}
        />
      </SecurityDashboardCard>
    </Box>
  );
};

export default EndpointEventsTab;
